import React, { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useUserContext } from "context/UserContext";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";
import { deriveThirdPartyId, deriveThirdPartyName } from "layouts/agent/utils";
import { ROLE_LEVEL_LABELS, normalizeRoleLevel } from "utils/roleUtils";

const FORMAL_ROLE_LABELS = {
  1: ROLE_LEVEL_LABELS[1],
  2: ROLE_LEVEL_LABELS[2],
  3: ROLE_LEVEL_LABELS[3],
  4: ROLE_LEVEL_LABELS[4],
  5: ROLE_LEVEL_LABELS[5],
  ADMIN: ROLE_LEVEL_LABELS[1],
  MANAGER: ROLE_LEVEL_LABELS[2],
  USER: ROLE_LEVEL_LABELS[3],
  SITE_OPERATOR: ROLE_LEVEL_LABELS[5],
};

const normalizeRoleToken = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();

const resolveRoleRank = (item) => {
  const candidates = [item?.role_rank, item?.roleRank, item?.role_level, item?.roleLevel];
  for (const candidate of candidates) {
    const numeric = Number(candidate);
    if (Number.isInteger(numeric) && numeric > 0) return numeric;
  }
  return null;
};

const canAssignOptionByRoleRank = (actorRoleRank, optionRoleRank) => {
  if (!Number.isInteger(actorRoleRank) || !Number.isInteger(optionRoleRank)) return null;
  if (actorRoleRank === 4) return actorRoleRank < optionRoleRank;
  return actorRoleRank <= optionRoleRank;
};

const formatFormalRoleLabel = (item) => {
  if (item?.role_name_v2) return String(item.role_name_v2);
  const directKeys = [
    item?.role_code,
    item?.roleCode,
    item?.role_rank,
    item?.roleRank,
    item?.role_level,
    item?.roleLevel,
    item?.id,
  ];

  for (const key of directKeys) {
    const match = FORMAL_ROLE_LABELS[normalizeRoleToken(key)];
    if (match) return match;
  }

  if (item?.role_name) return String(item.role_name);
  if (item?.roleName) return String(item.roleName);

  const name = normalizeRoleToken(item?.name);
  if (FORMAL_ROLE_LABELS[name]) return FORMAL_ROLE_LABELS[name];
  return item?.name ? String(item.name) : "";
};

const formatFormalRoleDisplay = (item, fallback = "") => {
  if (item?.role_name_v2) return String(item.role_name_v2);
  const tokens = [];
  if (Array.isArray(item?.roles)) tokens.push(...item.roles);
  if (item?.role) tokens.push(...String(item.role).split(","));

  const mapped = tokens
    .map((token) => FORMAL_ROLE_LABELS[normalizeRoleToken(token)] || "")
    .filter(Boolean);

  if (mapped.length) return mapped.join(", ");
  const directLabel = formatFormalRoleLabel(item);
  if (directLabel) return directLabel;
  return fallback;
};

function UserManagement() {
  const { roleLevel, userData } = useUserContext() || {};
  const canView = [1, 2, 3, 4].includes(normalizeRoleLevel(roleLevel));
  const backendUrl = useMemo(getGraphQLEndpoint, []);
  const email = userData?.email ?? "";
  const currentUserRoleRank = useMemo(() => normalizeRoleLevel(roleLevel), [roleLevel]);
  const isRecordNotFound = (err) =>
    String(err || "")
      .toLowerCase()
      .includes("record not found");
  const [open, setOpen] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const selectMenuProps = useMemo(
    () => ({
      PaperProps: {
        sx: {
          maxHeight: 320,
        },
      },
    }),
    []
  );
  const selectFieldSx = useMemo(
    () => ({
      "& .MuiInputBase-root": {
        minHeight: 52,
      },
      "& .MuiSelect-select": {
        display: "flex",
        alignItems: "center",
        paddingTop: "14px",
        paddingBottom: "14px",
      },
    }),
    []
  );
  const [formValues, setFormValues] = useState({
    target_email: "",
    target_first_name: "",
    target_last_name: "",
    target_role_id: "",
    target_third_party_id: "",
    iam_start_time: "",
    iam_end_time: "",
    description: "",
  });

  const [userRows, setUserRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [thirdPartyOptions, setThirdPartyOptions] = useState([]);
  const [thirdPartyError, setThirdPartyError] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState("");

  const thirdPartyLabelById = useMemo(() => {
    const map = new Map();
    thirdPartyOptions.forEach((option) => {
      if (option?.value === undefined || option?.value === null) return;
      map.set(String(option.value), option.label || String(option.value));
    });
    return map;
  }, [thirdPartyOptions]);

  const flattenThirdParties = (nodes) => {
    if (!Array.isArray(nodes)) return [];
    const stack = [...nodes];
    const result = [];
    while (stack.length) {
      const node = stack.shift();
      if (!node) continue;
      result.push(node);
      const children = node.children || node.Children;
      if (Array.isArray(children) && children.length) {
        stack.push(...children);
      }
    }
    return result;
  };

  useEffect(() => {
    if (!email) return;
    const fetchData = async () => {
      setLoading(true);
      setError("");
      setThirdPartyError("");
      try {
        const res = await fetch(backendUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query: `
              query GetIamRoles($email: String!) {
                GetIamRoles(email: $email)
              }
            `,
            variables: { email },
          }),
        });
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        const json = await res.json();
        if (json?.errors?.length) {
          const message = json.errors[0]?.message || "GraphQL error";
          if (isRecordNotFound(message)) {
            setRoleOptions([]);
            setUserRows([]);
          } else {
            throw new Error(message);
          }
        }
        if (!json?.errors?.length) {
          const payload = json?.data?.GetIamRoles;
          const data = payload?.data || {};
          const iamList = Array.isArray(data.iam_list) ? data.iam_list : [];
          const roles = Array.isArray(data.role) ? data.role : [];
          const roleList = Array.isArray(data.role_list) ? data.role_list : [];
          const currentRoleName = roles
            .map((item) => item?.name)
            .filter(Boolean)
            .join(", ");
          const currentEmail = data.iam?.email;

          const normalizedRoleOptions = roleList
            .map((item) => {
              const value = item?.role_level ?? item?.id ?? "";
              if (value === "" || value === null || value === undefined) return null;
              const roleRank = resolveRoleRank(item);
              const rankBasedDecision = canAssignOptionByRoleRank(currentUserRoleRank, roleRank);
              if (rankBasedDecision === false) return null;
              return {
                value: String(value),
                label: formatFormalRoleLabel(item) || (item?.name ? item.name : `Role ${value}`),
                roleRank,
              };
            })
            .filter(Boolean);

          setRoleOptions(normalizedRoleOptions);
          if (normalizedRoleOptions.length) {
            setFormValues((prev) => ({
              ...prev,
              target_role_id: prev.target_role_id || normalizedRoleOptions[0].value,
            }));
          }
          setUserRows(
            iamList.map((item) => {
              const roleFromItem = formatFormalRoleDisplay(item, item?.role || "");
              return {
                email: item.email || "",
                first_name: item.first_name || "",
                last_name: item.last_name || "",
                status: item.status || "",
                third_party_id: item.third_party_id ?? "",
                role: roleFromItem || (item.email === currentEmail ? currentRoleName : ""),
              };
            })
          );
        }
      } catch (err) {
        console.error(err);
        setError("載入使用者資料失敗");
      }
      try {
        const res = await fetch(backendUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query: `
              query GetMdmThirdPartyList($email: String!) {
                GetMdmThirdPartyList(email: $email)
              }
            `,
            variables: { email },
          }),
        });
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        const json = await res.json();
        if (json?.errors?.length) {
          throw new Error(json.errors[0]?.message || "GraphQL error");
        }
        const payload = json?.data?.GetMdmThirdPartyList;
        const data = Array.isArray(payload?.data) ? payload.data : [];
        const flatNodes = flattenThirdParties(data);
        const options = flatNodes
          .map((node) => {
            const rawValue = deriveThirdPartyId(node);
            const numericValue = Number(rawValue);
            if (!rawValue || Number.isNaN(numericValue)) return null;
            return {
              value: String(numericValue),
              label: deriveThirdPartyName(node),
            };
          })
          .filter(Boolean);

        setThirdPartyOptions(options);
        if (options.length) {
          setFormValues((prev) => ({
            ...prev,
            target_third_party_id: prev.target_third_party_id || options[0].value,
          }));
        }
      } catch (err) {
        console.error(err);
        setThirdPartyError("載入第三方失敗");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [backendUrl, currentUserRoleRank, email]);

  const rowsWithThirdPartyLabel = useMemo(
    () =>
      userRows.map((row) => {
        const rawId = row?.third_party_id;
        const key = rawId === null || rawId === undefined ? "" : String(rawId);
        const label = thirdPartyLabelById.get(key);
        return {
          ...row,
          third_party_label: label || key || "",
        };
      }),
    [thirdPartyLabelById, userRows]
  );

  const columns = useMemo(
    () => [
      { Header: "Email", accessor: "email" },
      { Header: "姓氏", accessor: "last_name" },
      { Header: "名字", accessor: "first_name" },
      { Header: "狀態", accessor: "status" },
      { Header: "綁定第三方", accessor: "third_party_label" },
      { Header: "角色", accessor: "role" },
    ],
    []
  );

  const handleChange = (key) => (event) => {
    setFormValues((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const resetCreateMessages = () => {
    setCreateError("");
    setCreateSuccess("");
  };

  const handleOpenCreate = () => {
    resetCreateMessages();
    setOpen(true);
  };

  const handleCloseCreate = () => {
    setOpen(false);
    resetCreateMessages();
  };

  const toISOIfPossible = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toISOString();
  };

  const handleCreate = async () => {
    resetCreateMessages();

    if (!email) {
      setCreateError("找不到登入者 Email");
      return;
    }

    const requiredFields = [
      { key: "target_email", label: "要創建的信箱" },
      { key: "target_role_id", label: "角色" },
      { key: "target_third_party_id", label: "綁定第三方" },
      { key: "target_first_name", label: "名字" },
      { key: "target_last_name", label: "姓氏" },
      { key: "iam_start_time", label: "帳號啟用時間" },
      { key: "iam_end_time", label: "帳號結束時間" },
      { key: "description", label: "描述" },
    ];

    const missing = requiredFields.find(({ key }) => !String(formValues[key] || "").trim());
    if (missing) {
      setCreateError(`請填寫 ${missing.label}`);
      return;
    }

    const payload = {
      requestor_email: email,
      target_email: formValues.target_email,
      target_role_id: Number(formValues.target_role_id),
      target_third_party_id: Number(formValues.target_third_party_id),
      target_first_name: formValues.target_first_name.trim(),
      target_last_name: formValues.target_last_name.trim(),
      iam_start_time: toISOIfPossible(formValues.iam_start_time),
      iam_end_time: toISOIfPossible(formValues.iam_end_time),
      description: formValues.description.trim(),
    };

    if (Number.isNaN(payload.target_role_id) || Number.isNaN(payload.target_third_party_id)) {
      setCreateError("角色或第三方格式不正確");
      return;
    }

    setCreating(true);
    try {
      const res = await fetch(backendUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `
            mutation CreateIamMission($input: IamCreateInput!) {
              CreateIamMission(input: $input)
            }
          `,
          variables: { input: payload },
        }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const json = await res.json();
      if (json?.errors?.length) {
        throw new Error(json.errors[0]?.message || "GraphQL error");
      }
      setCreateSuccess("已送出建立任務");
    } catch (err) {
      console.error(err);
      setCreateError("建立任務失敗");
    } finally {
      setCreating(false);
    }
  };

  if (!canView) return null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2.5}
                px={2.5}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <MDTypography variant="h6" color="white">
                  使用者管理
                </MDTypography>
                <MDButton variant="gradient" color="warning" onClick={handleOpenCreate}>
                  建立使用者
                </MDButton>
              </MDBox>
              <MDBox pt={3} px={3} pb={3}>
                {loading && (
                  <MDTypography variant="button" color="text" fontWeight="medium" mb={1}>
                    載入中...
                  </MDTypography>
                )}
                {error && (
                  <MDTypography variant="button" color="error" fontWeight="medium" mb={1}>
                    {error}
                  </MDTypography>
                )}
                <DataTable
                  table={{ columns, rows: rowsWithThirdPartyLabel }}
                  entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
                  canSearch
                  showTotalEntries={false}
                  isSorted
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={open}
        onClose={handleCloseCreate}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            px: 1,
          },
        }}
      >
        <MDBox display="flex" alignItems="center" justifyContent="space-between" px={3} pt={3}>
          <MDTypography variant="h6">建立使用者</MDTypography>
          <IconButton onClick={handleCloseCreate} size="small">
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
        <DialogContent>
          <Stack spacing={2.5}>
            <MDInput label="請求 Email" fullWidth value={email} disabled />
            <MDInput
              label="要創建的信箱"
              type="email"
              fullWidth
              value={formValues.target_email}
              onChange={handleChange("target_email")}
            />
            <MDInput
              label="名字"
              fullWidth
              value={formValues.target_first_name}
              onChange={handleChange("target_first_name")}
            />
            <MDInput
              label="姓氏"
              fullWidth
              value={formValues.target_last_name}
              onChange={handleChange("target_last_name")}
            />
            <MDInput
              select
              label="角色"
              fullWidth
              value={formValues.target_role_id}
              onChange={handleChange("target_role_id")}
              SelectProps={{ MenuProps: selectMenuProps }}
              sx={selectFieldSx}
            >
              {roleOptions.length ? (
                roleOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  無可用角色
                </MenuItem>
              )}
            </MDInput>
            <MDInput
              select
              label="綁定第三方"
              fullWidth
              value={formValues.target_third_party_id}
              onChange={handleChange("target_third_party_id")}
              SelectProps={{ MenuProps: selectMenuProps }}
              sx={selectFieldSx}
            >
              {thirdPartyOptions.length ? (
                thirdPartyOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  無可用第三方
                </MenuItem>
              )}
            </MDInput>
            {thirdPartyError && (
              <MDTypography variant="button" color="error" fontWeight="medium">
                {thirdPartyError}
              </MDTypography>
            )}
            <MDInput
              label="帳號啟用時間"
              type="datetime-local"
              fullWidth
              value={formValues.iam_start_time}
              onChange={handleChange("iam_start_time")}
              InputLabelProps={{ shrink: true }}
            />
            <MDInput
              label="帳號結束時間"
              type="datetime-local"
              fullWidth
              value={formValues.iam_end_time}
              onChange={handleChange("iam_end_time")}
              InputLabelProps={{ shrink: true }}
            />
            <MDInput
              label="描述"
              multiline
              rows={3}
              fullWidth
              value={formValues.description}
              onChange={handleChange("description")}
            />
            {createError && (
              <MDTypography variant="button" color="error" fontWeight="medium">
                {createError}
              </MDTypography>
            )}
            {createSuccess && (
              <MDTypography variant="button" color="success" fontWeight="medium">
                {createSuccess}
              </MDTypography>
            )}
            <MDButton
              variant="gradient"
              color="info"
              fullWidth
              onClick={handleCreate}
              disabled={creating}
            >
              建立使用者
            </MDButton>
          </Stack>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default UserManagement;
